import { Injectable, Signal, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filterMapSomes } from '@fmnts/core/rxjs';
import { DeclarativeGeolocationService } from '@fmnts/declarative-forms/geolocation';
import {
  GeoCoordinates,
  GeolocationService,
} from '@formunauts/shared/geolocation/data-access';
import { Store } from '@ngrx/store';
import { Option } from 'effect';
import { type Observable } from 'rxjs';
import { selectCurrentNextLocationCoordinates } from '../locations/data-access';

/**
 * Service to access the users geolocation.
 */
@Injectable({ providedIn: 'root' })
export class NextGeolocationSerivce extends DeclarativeGeolocationService {
  private readonly store = inject(Store);
  private readonly geolocation = inject(GeolocationService);

  /** Coordinates of the location that the user is checked-in at. */
  private readonly _locationCoordinates = toSignal(
    this.store.select(selectCurrentNextLocationCoordinates),
    { requireSync: true },
  );

  /** Coordinates of the location that the user is checked-in at. */
  private readonly _providerCoordinates = computed(() =>
    Option.flatMap(this._locationCoordinates(), Option.fromNullable),
  );

  readonly coordinates: Signal<Option.Option<GeoCoordinates>> = computed(() =>
    Option.firstSomeOf([
      Option.fromNullable(this.geolocation.coordinates()),
      this._providerCoordinates(),
    ]),
  );

  readonly canImproveAccuracy = computed(
    () =>
      this.geolocation.permissionDenied() &&
      this._locationCoordinates() === null,
  );

  readonly latestCoordinates$: Observable<GeoCoordinates> = toObservable(
    this.coordinates,
  ).pipe(filterMapSomes());

  override updatePosition() {
    this.geolocation.updatePosition();
  }
}
